'use strict';

/**
 * Module du formulaire de contact (page /fr/contact).
 *
 * Particularités à connaître :
 * - Le formulaire est un formulaire Neos.Form rendu par Fluid. Le `<form>` est en
 *   `method="post"` + `novalidate` : la validation HTML5 native est désactivée, c'est
 *   donc notre validation JS (classes `is-invalid` / `was-validated`) qui pilote l'UX.
 * - La soumission est NATIVE (POST classique du navigateur), volontairement pas en
 *   fetch/AJAX : l'ancienne version en `fetch()` cassait l'UX sur mobile (retour
 *   silencieux sur /fr/contact au lieu d'afficher les erreurs serveur).
 * - Le type de demande (« Nous contacter », « Support », etc.) est une grille de
 *   tuiles : la tuile sélectionnée écrit son e-mail cible dans un champ caché
 *   (`.request-type-value`) et conditionne l'affichage de champs supplémentaires.
 */

/**
 * Point d'entrée du module : câble la grille de types de demande et la soumission.
 *
 * Tout est gardé derrière la présence de `.request-type-value` car ce champ caché
 * n'existe que sur le vrai formulaire de contact (et pas sur d'autres formulaires).
 */
const init = () => {
    const contactForm = document.querySelector('.tc-website-contactform')

    if (contactForm) {
        const requestTypeValue = contactForm.querySelector('.request-type-value')

        if (requestTypeValue) {
            const emergencyRequestField = contactForm.querySelector('.tc-website-contactformemergencyrequest')
            const estimatedBudgetField = contactForm.querySelector('.tc-website-contactformestimatedbudget')
            const formElement = contactForm.getElementsByTagName('form')[0]

            // État initial : afficher/masquer les champs conditionnels selon le type pré-sélectionné.
            if (emergencyRequestField) {
                updateEmergencyRequestField(emergencyRequestField, requestTypeValue.value)
            }
            if (estimatedBudgetField) {
                updateEstimatedBudgetField(estimatedBudgetField, requestTypeValue.value)
            }

            contactForm.querySelectorAll('.tc-website-contactformrequesttypeitem').forEach((requestTypeItem) => {
                requestTypeItem.addEventListener('click', () => {
                    updateSelectedRequestType(contactForm, requestTypeItem, requestTypeValue, emergencyRequestField, estimatedBudgetField)
                })

                // Permet de pré-sélectionner une tuile via l'ancre d'URL (ex. /fr/contact#support).
                if (window.location.hash && requestTypeItem.id && window.location.hash === '#' + requestTypeItem.id) {
                    updateSelectedRequestType(contactForm, requestTypeItem, requestTypeValue, emergencyRequestField, estimatedBudgetField)
                }
            })

            formElement.addEventListener('submit', (event) => {
                // Soumission native : on laisse le navigateur poster le formulaire et on
                // ne bloque (preventDefault) QUE si la validation client a échoué.
                if (!validateContactForm(formElement)) {
                    event.preventDefault()
                }
            })
        }
    }
}

/**
 * Sélectionne une tuile de type de demande et synchronise l'état du formulaire.
 *
 * Effets : retire la classe `selected` de la tuile courante, l'applique à la nouvelle,
 * écrit l'e-mail cible (`data-email` de la tuile) dans le champ caché `.request-type-value`,
 * puis met à jour les champs conditionnels (urgence / budget estimé).
 *
 * Note : on suppose qu'il existe toujours exactement une tuile `.selected` au moment
 * du clic (cas garanti par le rendu Fluid initial).
 */
const updateSelectedRequestType = (contactForm, requestTypeItem, requestTypeValue, emergencyRequestField, estimatedBudgetField) => {
    contactForm.querySelector('.tc-website-contactformrequesttypeitem.selected').classList.remove('selected')
    requestTypeItem.classList.add('selected')
    requestTypeValue.value = requestTypeItem.dataset.email
    if (emergencyRequestField) {
        updateEmergencyRequestField(emergencyRequestField, requestTypeItem.dataset.email)
    }

    if (estimatedBudgetField) {
        updateEstimatedBudgetField(estimatedBudgetField, requestTypeItem.dataset.email)
    }
}

/**
 * Valide le formulaire côté client et prépare la soumission native.
 *
 * Retourne un booléen utilisé par le handler `submit` :
 * - `false` : la validation a échoué -> l'appelant fait `preventDefault()` (envoi bloqué),
 *   les champs fautifs reçoivent la classe `is-invalid` et le formulaire `was-validated`.
 * - `true`  : tout est valide -> l'appelant laisse le navigateur poster le formulaire ;
 *   on en profite pour garantir le champ de pagination Neos.Form et afficher le loader.
 *
 * Le `checkValidity()` fonctionne malgré `novalidate` sur le `<form>` : `novalidate`
 * désactive seulement l'UI native du navigateur, pas l'API de contrainte.
 */
const validateContactForm = (formElement) => {
    // Regex e-mail volontairement permissive (validation serveur Neos.Form fait foi ensuite).
    const emailRegex = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/

    if (formElement.checkValidity() === false) {
        const nameInput = formElement.querySelector('#contact-form-name')
        const emailInput = formElement.querySelector('#contact-form-email')
        const phoneInput = formElement.querySelector('#contact-form-phone')
        const messageInput = formElement.querySelector('#contact-form-message')
        const privacyPolicyConsentInput = formElement.querySelector('#contact-form-privacy-policy-consent')

        if (nameInput.value === '') {
            nameInput.classList.add('is-invalid')
        }

        if (emailInput.value === '' || !emailRegex.test(emailInput.value)) {
            emailInput.classList.add('is-invalid')
        }

        if (phoneInput.value === '') {
            phoneInput.classList.add('is-invalid')
        }

        if (messageInput.value === '') {
            messageInput.classList.add('is-invalid')
        }

        if (privacyPolicyConsentInput.checked === false) {
            privacyPolicyConsentInput.classList.add('is-invalid')
        }

        // Pour chaque champ en erreur, on attache un écouteur qui retire `is-invalid`
        // dès que l'utilisateur corrige la saisie (feedback immédiat, sans re-soumettre).
        // Les écouteurs sont (ré)attachés à chaque tentative ; sans effet de bord notable
        // car ils ne font que retirer une classe.
        formElement.querySelectorAll('.is-invalid').forEach((formInput) => {
            if (formInput.tagName === 'TEXTAREA' || formInput.getAttribute('type') === 'text') {
                formInput.addEventListener('keyup', () => {
                    if (formInput.getAttribute('id') === 'contact-form-email') {
                        // L'e-mail n'est « réparé » que s'il passe la regex, pas juste s'il est non vide.
                        if (emailRegex.test(formInput.value)) {
                            formInput.classList.remove('is-invalid')
                        }
                    } else {
                        if (formInput.value !== '') {
                            formInput.classList.remove('is-invalid')
                        }
                    }
                })
            } else if (formInput.getAttribute('type') === 'checkbox') {
                formInput.addEventListener('change', () => {
                    if (formInput.checked) {
                        formInput.classList.remove('is-invalid')
                    }
                })
            }
        })

        // `was-validated` active les styles d'erreur globaux (cf. SCSS) sur le formulaire.
        formElement.classList.add('was-validated')

        return false
    }

    // Soumission NATIVE du navigateur (POST classique, pas de fetch) : le serveur
    // redirige vers la page de confirmation en cas de succès, ou re-render le
    // formulaire avec ses messages d'erreur en cas d'échec. Plus fiable sur mobile.
    ensureCurrentPageField(formElement)
    showFormLoader()

    return true
}

/**
 * Garantit la présence du champ caché de pagination Neos.Form (`--contact-form[__currentPage]`).
 *
 * Neos.Form a besoin de connaître la page courante du formulaire pour traiter le POST.
 * En temps normal ce champ est déjà émis par le bouton « Envoyer » (`<f:form.button
 * name="__currentPage" value="1">`) ; ce helper n'est qu'un garde-fou idempotent : il
 * n'ajoute le champ que s'il manque, avec la même valeur `1`, donc sans double envoi.
 */
const ensureCurrentPageField = (formElement) => {
    if (formElement.querySelector('input[name="--contact-form[__currentPage]"]')) {
        return
    }

    const currentPageInput = document.createElement('input')
    currentPageInput.type = 'hidden'
    currentPageInput.name = '--contact-form[__currentPage]'
    currentPageInput.value = '1'
    formElement.appendChild(currentPageInput)
}

/**
 * Affiche le loader plein écran pendant la navigation déclenchée par la soumission native.
 *
 * Comme l'envoi est natif (la page va se recharger), le loader n'a pas besoin d'être
 * retiré : il disparaît avec le changement de page. C'est volontaire.
 */
const showFormLoader = () => {
    const tcLoader = document.createElement('div')
    tcLoader.classList.add('techniconcept-loader', 'visible', 'fullscreen', 'form-loader')

    const greenBar = document.createElement('div')
    greenBar.classList.add('green-bar')
    tcLoader.appendChild(greenBar)

    document.body.appendChild(tcLoader)
}

/**
 * Affiche le champ « demande urgente » uniquement pour le type de demande Support.
 *
 * Le rattachement se fait par l'e-mail cible de la tuile (`support@techniconcept.ch`),
 * pas par un identifiant de type : c'est cet e-mail qui sert de discriminant côté formulaire.
 */
const updateEmergencyRequestField = (emergencyRequestField, email) => {
    if (email === 'support@techniconcept.ch') {
        emergencyRequestField.classList.remove('hidden')
    } else {
        emergencyRequestField.classList.add('hidden')
    }
}

/**
 * Affiche le champ « budget estimé » uniquement pour le type de demande Solutions/Offre.
 *
 * Même logique que ci-dessus : discriminant par e-mail cible (`solutions@techniconcept.ch`).
 */
const updateEstimatedBudgetField = (estimatedBudgetField, email) => {
    if (email === 'solutions@techniconcept.ch') {
        estimatedBudgetField.classList.remove('hidden')
    } else {
        estimatedBudgetField.classList.add('hidden')
    }
}

export default {init}
